<template>
    <Dialog v-model:visible="visibleLocal" modal :header="dialogTitle" :style="{ width: '520px' }"
        :closable="!isCreating" :dismissableMask="!isCreating">
        <div class="flex flex-col gap-4">
            <Message v-if="step === 1 && shouldShowBillingWarning" severity="info" :closable="false">{{
                t('settings.projects.create.billing_notice') }}</Message>

            <div v-if="step === 1" class="flex flex-col gap-4">
                <!-- MYAC-1306 — `lockType`: a host that needs ONE type (the
                     regulatory monitoring's « Connecter mon TD » connects what it
                     creates as a TD) shows that type's row alone, with its quota
                     and upgrade hint, and no way to pick another. -->
                <div class="flex flex-col gap-3" data-testid="project-type-choices">
                    <div v-if="showTypeRow(ProjectCreationRequestType.QMS)" class="flex items-center justify-between gap-2">
                        <div class="flex items-center gap-2 min-w-0">
                            <RadioButton v-model="projectType" :value="ProjectCreationRequestType.QMS"
                                inputId="project-type-qms" :disabled="!canCreateQms" />
                            <label for="project-type-qms" class="text-sm text-color cursor-pointer">{{
                                t('projects.create.create_qms_option') }}</label>
                            <span v-if="hasQmsFeature && !canCreateQms" class="text-xs text-color-secondary whitespace-nowrap">({{ t('projects.create.quota_reached') }})</span>
                        </div>
                        <UpgradePlanButton v-if="!canCreateQms" />
                    </div>
                    <div v-if="showTypeRow(ProjectCreationRequestType.TD)" class="flex items-center justify-between gap-2">
                        <div class="flex items-center gap-2 min-w-0">
                            <RadioButton v-model="projectType" :value="ProjectCreationRequestType.TD"
                                inputId="project-type-td" :disabled="!canCreateTd" />
                            <label for="project-type-td" class="text-sm text-color cursor-pointer">{{
                                t('projects.create.create_product_option') }}</label>
                            <span v-if="hasTdFeature && !canCreateTd" class="text-xs text-color-secondary whitespace-nowrap">({{ t('projects.create.quota_reached') }})</span>
                        </div>
                        <UpgradePlanButton v-if="!canCreateTd" />
                    </div>
                    <div v-if="showTypeRow(ProjectCreationRequestType.REGULATORY_MONITORING)" class="flex items-center justify-between gap-2">
                        <div class="flex items-center gap-2 min-w-0">
                            <RadioButton v-model="projectType"
                                :value="ProjectCreationRequestType.REGULATORY_MONITORING"
                                inputId="project-type-regulatory-monitoring" :disabled="!canCreateRegMon" />
                            <label for="project-type-regulatory-monitoring"
                                class="text-sm text-color cursor-pointer">{{
                                    t('projects.create.create_regulatory_monitoring_option') }}</label>
                        </div>
                        <UpgradePlanButton v-if="!canCreateRegMon" />
                    </div>
                </div>

                <div class="flex flex-col gap-2">
                    <label class="text-sm text-color">{{ projectNameLabel }} <span
                            class="text-red-500">{{ t('projects.create.required_indicator') }}</span></label>
                    <InputText v-model="projectName" :placeholder="projectNamePlaceholder" required
                        :disabled="!canCreate" />
                    <small v-if="projectNameError" class="text-red-500">{{ t('projects.create.field_required')
                        }}</small>
                    <Message v-if="isTdSelected" severity="warn" :closable="false" class="mt-2">
                        {{ t('projects.create.product_name_warning') }}
                    </Message>
                </div>

                <div class="flex flex-col gap-2">
                    <label class="text-sm text-color">{{ t('projects.create.company_name_label') }} <span
                            class="text-red-500">{{ t('projects.create.required_indicator') }}</span></label>
                    <InputText v-model="companyName" :placeholder="t('projects.create.company_name_placeholder')"
                        required :disabled="!canCreate" />
                    <small v-if="companyNameError" class="text-red-500">{{ t('projects.create.field_required')
                        }}</small>
                    <Message v-if="projectType === ProjectCreationRequestType.QMS" severity="warn" :closable="false" class="mt-2">
                        {{ t('projects.create.company_name_warning') }}
                    </Message>
                </div>

                <template v-if="isTdSelected">
                    <div class="flex flex-col gap-2">
                        <label class="text-sm text-color">{{ t('projects.create.td_regulation_label') }} <span
                                class="text-red-500">{{ t('projects.create.required_indicator') }}</span></label>
                        <Select v-model="tdRegulation" :options="tdRegulationOptions" optionLabel="label"
                            optionValue="value" :disabled="!canCreate" />
                    </div>
                    <div class="flex flex-col gap-2">
                        <label class="text-sm text-color">{{ t('projects.create.parent_qms_label') }}</label>
                        <Select v-model="parentQmsProjectId" :options="parentQmsOptions" optionLabel="label"
                            optionValue="value" :placeholder="t('projects.create.parent_qms_placeholder')"
                            :disabled="!canCreate" />
                    </div>
                    <div class="flex flex-col gap-2">
                        <label class="text-sm text-color">{{ t('projects.create.market_placement_date_label') }}</label>
                        <DatePicker v-model="marketPlacementDate" dateFormat="yy-mm-dd" :showIcon="true" :showButtonBar="true" :disabled="!canCreate" />
                        <small class="text-color-secondary">{{ t('projects.create.market_placement_date_hint') }}</small>
                    </div>
                </template>
                <Message v-if="errorMessage" severity="error" :closable="false">{{ errorMessage }}</Message>
            </div>

            <div v-else-if="step === 2" class="flex flex-col gap-3">
                <div class="flex items-center gap-2">
                    <div class="text-sm text-color">{{ t('projects.create.assign_roles') }}</div>
                    <RoleInfoButton />
                </div>
                <div v-if="isCreating" class="flex items-center gap-2 text-sm text-color">
                    <ProgressSpinner style="width:20px;height:20px" strokeWidth="4" />
                    <span>{{ t('projects.create.creating_project') }}</span>
                </div>
                <div v-if="tenantMembers.length === 0 && pendingInvitations.length === 0" class="text-color-secondary text-sm">{{
                    t('projects.create.no_members_in_tenant') }}</div>
                <div v-else class="flex flex-col gap-3 max-h-64 overflow-y-auto pr-1">
                    <div v-for="m in tenantMembers" :key="m.uid" class="flex items-center justify-between gap-3">
                        <div class="flex-1 truncate">
                            <span class="text-sm text-color">{{ memberLabel(m) }}</span>
                        </div>
                        <Select :options="roleOptions" optionLabel="label" optionValue="value" class="w-40"
                            v-model="memberRoles[m.uid!]" :disabled="!canCreate" />
                    </div>

                    <!-- MYAC-1176 — people invited to the company who have not
                         accepted yet. They get no membership now; the project is
                         written onto their invitation and becomes real access the
                         moment they accept. -->
                    <template v-if="pendingInvitations.length">
                        <div class="text-xs text-color-secondary pt-2">{{ t('projects.create.pending_invitations_hint') }}</div>
                        <div v-for="inv in pendingInvitations" :key="inv.id" class="flex items-center justify-between gap-3">
                            <div class="flex-1 truncate flex items-center gap-2">
                                <span class="text-sm text-color truncate">{{ inv.email }}</span>
                                <Tag :value="t('settings.users.invitation_pending_tag')" severity="warn" />
                            </div>
                            <Select :options="roleOptions" optionLabel="label" optionValue="value" class="w-40"
                                v-model="invitationRoles[inv.id]" :disabled="!canCreate" />
                        </div>
                    </template>
                </div>
                <Message v-if="errorMessage" severity="error" :closable="false">{{ errorMessage }}</Message>
            </div>

            <div v-else-if="step === 3" class="flex flex-col gap-3">
                <div class="flex flex-col items-center gap-2">
                    <div class="flex items-center gap-2 text-sm text-color">
                        <ProgressSpinner style="width:20px;height:20px" strokeWidth="4" />
                        <span>{{ t('projects.create.creating_project') }}</span>
                    </div>
                    <span class="text-xs text-color-secondary">{{ t('shared.loading_may_take_time') }}</span>
                </div>
                <Message v-if="errorMessage" severity="error" :closable="false">{{ errorMessage }}</Message>
            </div>

            <div v-else-if="step === 4" class="flex flex-col gap-3">
                <Message severity="success" :closable="false">{{ t('projects.create.created_success', {
                    name:
                    projectName }) }}
                </Message>
            </div>

            <div v-else-if="step === 5" class="flex flex-col gap-3">
                <template v-if="errorCode === ProjectCreationRequestErrorCode.PAYMENT_METHOD_REQUIRED">
                    <Message severity="warn" :closable="false">{{ t('projects.create.error_payment_method_required') }}
                    </Message>
                </template>
                <template v-else>
                    <Message severity="error" :closable="false">{{ t('projects.create.error_generic') }}</Message>
                    <span v-if="stripeErrorCode" class="text-xs text-color-secondary">
                        {{ t('projects.create.error_reference_code', { code: stripeErrorCode }) }}
                    </span>
                </template>
            </div>
        </div>

        <template #footer>
            <div v-if="step === 1" class="flex justify-end gap-2">
                <Button :label="t('shared.cancel')" severity="secondary" text @click="onCancel"
                    :disabled="isCreating" />
                <Button :label="t('shared.next')" :disabled="!canGoNext || isCreating || !canCreate" @click="onNext" />
            </div>
            <div v-else-if="step === 2" class="flex justify-end gap-2">
                <Button :label="t('shared.back')" severity="secondary" text @click="onBack" :disabled="isCreating" />
                <Button :label="t('shared.cancel')" severity="secondary" text @click="onCancel"
                    :disabled="isCreating" />
                <Button :label="t('projects.create.create')" :disabled="!canCreate || isCreating" @click="onCreate" />
            </div>
            <div v-else-if="step === 3" class="flex justify-end gap-2">
                <Button :label="t('shared.cancel')" severity="secondary" text :disabled="true" />
            </div>
            <div v-else-if="step === 4" class="flex justify-end gap-2">
                <Button :label="t('shared.close')" severity="secondary" text @click="onCancel" />
                <Button :label="t('projects.create.go_to_project')" :disabled="!createdProjectId"
                    @click="goToProject" />
            </div>
            <div v-else-if="step === 5" class="flex justify-end gap-2">
                <Button :label="t('shared.close')" severity="secondary" text @click="onCancel" />
                <OpenCustomerPortalButton v-if="errorCode === ProjectCreationRequestErrorCode.PAYMENT_METHOD_REQUIRED"
                    :label="t('projects.create.error_payment_add_button')" />
            </div>
        </template>
    </Dialog>

</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import DatePicker from 'primevue/datepicker'
import RadioButton from 'primevue/radiobutton'
import Button from 'primevue/button'
import Message from 'primevue/message'
import ProgressSpinner from 'primevue/progressspinner'
import Tag from 'primevue/tag'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'

import { useTenantsStore } from '@/stores/tenants'
import { useMembersStore } from '@/stores/members'
import { useUsersStore } from '@/stores/users'
import { useBillingStore } from '@/stores/billing'
import { useProjectsStore } from '@/stores/projects'
import { useFeaturesStore } from '@/stores/features'
import { ProjectCreationRequestType, ProjectCreationRequestStatus, ProjectCreationRequestErrorCode, isEditableInvitation } from '@/models/Billing'
import { useAppStore } from '@/stores/app'
import { logger } from '@/utils/logger'
import { breaksNameRule } from '@/utils/nameRule'
import { projectRouteBase } from '@/models/Project'
import type { TDRegulation } from '@/models/td'
import RoleInfoButton from '@/components/shared/RoleInfoButton.vue'
import OpenCustomerPortalButton from '@/components/settings/billing/OpenCustomerPortalButton.vue'
import UpgradePlanButton from '@/components/shared/UpgradePlanButton.vue'
import { memberLabel } from '@/utils/userDisplay'

const props = defineProps<{
    visible: boolean
    initialType?: ProjectCreationRequestType
    /** MYAC-1306 — the host keeps the user where they are: on DONE the dialog
     * closes itself (after `created`) instead of showing its "go to the
     * project" success step. Default false: every other caller unchanged. */
    stayAfterCreate?: boolean
    /** MYAC-1306 — only `initialType` can be created: the other types' rows
     * are not shown. Ignored without an `initialType`. */
    lockType?: boolean
}>()
const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void
    /** MYAC-1306 — fired once the creation request is DONE, with the new
     * project's id and the name it was created under. */
    (e: 'created', projectId: string, projectName: string): void
}>()

const tenantsStore = useTenantsStore()
const { currentTenant } = storeToRefs(tenantsStore)
const membersStore = useMembersStore()
const usersStore = useUsersStore()
const billingStore = useBillingStore()
const projectsStore = useProjectsStore()
const featuresStore = useFeaturesStore()
const { t } = useI18n()

const visibleLocal = ref(props.visible)
watch(() => props.visible, (v) => (visibleLocal.value = v))
watch(visibleLocal, (v) => emit('update:visible', v))

// Tenant data kept up-to-date automatically via real-time subscription

const projectName = ref('')
const companyName = ref('')
const parentQmsProjectId = ref<string | null>(null)
const marketPlacementDate = ref<Date | null>(null)
const tdRegulation = ref<TDRegulation>('mdr')

const tdRegulationOptions = computed(() => [
  { label: 'MDR 2017/745', value: 'mdr' as TDRegulation },
  { label: 'IVDR 2017/746', value: 'ivdr' as TDRegulation },
])

const isTdSelected = computed(() => projectType.value === ProjectCreationRequestType.TD)

const parentQmsOptions = computed(() => {
  const qmsProjects = projectsStore.tenantProjects
    .filter((p) => p.type === 'qms')
    .slice()
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }))
  return [
    { label: t('shared.none'), value: null },
    ...qmsProjects.map((p) => ({ label: p.name, value: p.id })),
  ]
})
const defaultProjectType = computed(() => {
    if (featuresStore.canCreateProjectOfType(ProjectCreationRequestType.QMS)) return ProjectCreationRequestType.QMS
    if (featuresStore.canCreateProjectOfType(ProjectCreationRequestType.TD)) return ProjectCreationRequestType.TD
    if (featuresStore.canCreateProjectOfType(ProjectCreationRequestType.REGULATORY_MONITORING)) return ProjectCreationRequestType.REGULATORY_MONITORING
    return ProjectCreationRequestType.QMS
})
const projectType = ref<ProjectCreationRequestType>(props.initialType ?? defaultProjectType.value)

/** MYAC-1306 — every type's row, or only the locked one. */
function showTypeRow(type: ProjectCreationRequestType): boolean {
    return !(props.lockType && props.initialType) || props.initialType === type
}
const step = ref(1)
const isCreating = ref(false)
const errorMessage = ref<string | null>(null)
const createdProjectId = ref<string | null>(null)
const errorCode = ref<ProjectCreationRequestErrorCode | null>(null)
const stripeErrorCode = ref<string | null>(null)
const router = useRouter()
const validationAttempted = ref(false)

const dialogTitle = computed(() => {
    if (step.value === 2) {
        return t('projects.create.manage_users_title')
    }
    if (projectType.value === ProjectCreationRequestType.TD) {
        return t('projects.create.dialog_title_product')
    }
    if (projectType.value === ProjectCreationRequestType.REGULATORY_MONITORING) {
        return t('projects.create.dialog_title_regulatory_monitoring')
    }
    return t('projects.create.dialog_title_qms')
})

const projectNameLabel = computed(() => {
    if (projectType.value === ProjectCreationRequestType.TD) {
        return t('projects.create.product_name_label')
    }
    if (projectType.value === ProjectCreationRequestType.REGULATORY_MONITORING) {
        return t('projects.create.regulatory_monitoring_name_label')
    }
    return t('projects.create.qms_name_label')
})

const projectNamePlaceholder = computed(() => {
    if (projectType.value === ProjectCreationRequestType.TD) {
        return t('projects.create.product_name_placeholder')
    }
    if (projectType.value === ProjectCreationRequestType.REGULATORY_MONITORING) {
        return t('projects.create.regulatory_monitoring_name_placeholder')
    }
    return t('projects.create.qms_name_placeholder')
})

type RoleOption = { label: string; value: 'none' | 'member' | 'admin' }
const roleOptions: RoleOption[] = [
    { label: t('projects.create.role_not_member'), value: 'none' },
    { label: t('projects.create.role_member'), value: 'member' },
    { label: t('projects.create.role_admin'), value: 'admin' },
]

const memberRoles = reactive<Record<string, 'none' | 'member' | 'admin'>>({})
/** MYAC-1176 — role picked for each still-open invitation, keyed by invitation id. */
const invitationRoles = reactive<Record<string, 'none' | 'member' | 'admin'>>({})

const tenantMembers = computed(() => {
    const list = membersStore.tenantMembers || []
    list.forEach((m) => {
        // MYAC-1522 (decision G): a company admin is in every project of the company
        // by default, a new one included (an admin outside them saw nothing). Credit
        // plans only: on a legacy seat plan each member added may buy a seat.
        const companyAdmin = m.role === 'admin' && m.active !== false && featuresStore.isCreditPlan
        if (m.uid && !memberRoles[m.uid]) memberRoles[m.uid] = companyAdmin ? 'admin' : 'none'
    })
    return list
})

/**
 * MYAC-1176 — people invited to the company who have not accepted yet. A
 * project created today used to be out of their reach until they accepted and
 * someone remembered to add them; now they can be given the project up front.
 */
const pendingInvitations = computed(() => {
    if (!featuresStore.isTenantAdmin) return []
    return (tenantsStore.invitations || []).filter((i) => isEditableInvitation(i) && !!i.id)
})

// Seeding the role pickers is a side effect, so it belongs in a watcher rather
// than in the getter the template renders from.
watch(pendingInvitations, (list) => {
    list.forEach((i) => {
        if (!invitationRoles[i.id]) invitationRoles[i.id] = 'none'
    })
}, { immediate: true })

onMounted(async () => {
    // Tenant data kept up-to-date automatically via real-time subscription
    if (currentTenant.value?.id) {
        await membersStore.listTenantMembers(currentTenant.value.id)
    }
    // MYAC-1176 — the pending-invitation list comes from a tenant-scoped
    // listener that other screens arm; make sure it is running here too.
    if (currentTenant.value?.id && featuresStore.isTenantAdmin && !tenantsStore.invitationsUnsub) {
        try { await tenantsStore.subscribeToInvitations() } catch (e) { logger.warn('ProjectCreateDialog: invitations subscribe failed', e) }
    }
    // Default current user as admin in the new project
    if (usersStore.uid && !memberRoles[usersStore.uid]) memberRoles[usersStore.uid] = 'admin'
    // Initialize company name from tenant name
    if (currentTenant.value?.name) {
        companyName.value = currentTenant.value.name
    }
})

watch(currentTenant, async (t) => {
    if (t?.id) {
        await membersStore.listTenantMembers(t.id)
    } else {
        membersStore.clearTenantMembers()
    }
    // Ensure default again on tenant switch
    if (usersStore.uid && !memberRoles[usersStore.uid]) memberRoles[usersStore.uid] = 'admin'
    // Update company name when tenant changes
    if (t?.name) {
        companyName.value = t.name
    } else {
        companyName.value = ''
    }
})

// When members load later, ensure default for current user once
watch(() => membersStore.tenantMembers.length, () => {
    if (usersStore.uid && !memberRoles[usersStore.uid]) memberRoles[usersStore.uid] = 'admin'
})

const canCreateQms = computed(() => featuresStore.canCreateProjectOfType(ProjectCreationRequestType.QMS))
const canCreateTd = computed(() => featuresStore.canCreateProjectOfType(ProjectCreationRequestType.TD))
const canCreateRegMon = computed(() => featuresStore.canCreateProjectOfType(ProjectCreationRequestType.REGULATORY_MONITORING))
const hasQmsFeature = computed(() => featuresStore.effectivePlanFeatures?.has_qms ?? false)
const hasTdFeature = computed(() => featuresStore.effectivePlanFeatures?.has_td ?? false)
const canCreate = computed(() => featuresStore.canCreateProjectOfType(projectType.value))
const canGoNext = computed(() => canCreate.value && !!projectName.value.trim() && !!companyName.value.trim() && !!currentTenant.value?.id && !!projectType.value)

const projectNameError = computed(() => validationAttempted.value && !projectName.value.trim())
const companyNameError = computed(() => validationAttempted.value && !companyName.value.trim())

// Check if billing warning should be shown — only for legacy per-seat plans
const shouldShowBillingWarning = computed(() => {
    if (featuresStore.isCreditPlan) return false
    const tenant = currentTenant.value
    if (!tenant?.services) return false

    if (projectType.value === ProjectCreationRequestType.QMS) {
        const quotaRemaining = tenant.services.quota_users_qms?.quota_remaining ?? 0
        return quotaRemaining <= 0
    }
    if (projectType.value === ProjectCreationRequestType.TD) {
        const quotaRemaining = tenant.services.quota_users_td?.quota_remaining ?? 0
        return quotaRemaining <= 0
    }
    if (projectType.value === ProjectCreationRequestType.REGULATORY_MONITORING) {
        return false
    }

    return false
})

function resetForm() {
    projectName.value = ''
    companyName.value = currentTenant.value?.name || ''
    parentQmsProjectId.value = null
    tdRegulation.value = 'mdr'
    projectType.value = props.initialType ?? defaultProjectType.value
    step.value = 1
    validationAttempted.value = false
    errorCode.value = null
    stripeErrorCode.value = null
    errorMessage.value = null
    Object.keys(memberRoles).forEach((k) => delete memberRoles[k])
    Object.keys(invitationRoles).forEach((k) => delete invitationRoles[k])
}

function onCancel() {
    visibleLocal.value = false
    resetForm()
}

function onNext() {
    validationAttempted.value = true
    if (!canGoNext.value) return
    errorMessage.value = null
    step.value = 2
}

function onBack() {
    // A step-2 error (no admin) is not about the names step 1 shows.
    errorMessage.value = null
    step.value = 1
}

function onCreate() {
    if (!canCreate.value) return
    errorMessage.value = null
    isCreating.value = true
    step.value = 3
    // Call billing store directly and watch for result
    const selectedAdminIds = Object.entries(memberRoles)
        .filter(([_, role]) => role === 'admin')
        .map(([uid]) => uid)
    const selectedMemberIds = Object.entries(memberRoles)
        .filter(([_, role]) => role === 'member')
        .map(([uid]) => uid)
    const admins = selectedAdminIds
    const members = Array.from(new Set<string>([...admins, ...selectedMemberIds]))
    if (!admins.length) {
        isCreating.value = false
        step.value = 2
        errorMessage.value = t('projects.create.at_least_one_admin_required')
        return
    }
    const request = billingStore.createProjectCreationRequest(
        currentTenant.value!.id,
        projectName.value.trim(),
        projectType.value,
        admins,
        members,
        companyName.value.trim(),
        isTdSelected.value ? parentQmsProjectId.value ?? undefined : undefined,
        isTdSelected.value ? tdRegulation.value : undefined,
        isTdSelected.value ? marketPlacementDate.value ?? undefined : undefined,
    )
    let stop: (() => void) | null = null
    stop = watch(
        () => billingStore.projectCreationRequest,
        (req) => {
            if (!req) return
            if (req.status === ProjectCreationRequestStatus.DONE) {
                stop?.()
                isCreating.value = false
                void projectsStore.listProjects(true)
                const pid = req.project?.id
                if (pid) {
                    createdProjectId.value = pid
                    // MYAC-1176 — the project only exists now, so the invitations
                    // can only be given it now.
                    void grantNewProjectToInvitations(pid)
                    emit('created', pid, projectName.value.trim())
                    if (props.stayAfterCreate) {
                        visibleLocal.value = false
                        resetForm()
                        return
                    }
                }
                step.value = 4
            } else if (req.status === ProjectCreationRequestStatus.FAILED) {
                stop?.()
                isCreating.value = false
                errorCode.value = req.error_code ?? null
                stripeErrorCode.value = req.stripe_error_code ?? null
                step.value = 5
            }
        },
        { immediate: true }
    )
    // MYAC-1571 — a refused write leaves no request, so the watch above never
    // fires: without this the dialog spins on « Creating » for good. When the
    // names are why (over 120 characters, < or >), back to the names; any other
    // refusal is the generic error.
    Promise.resolve(request).catch((e) => {
        logger.warn('ProjectCreateDialog: creation request refused', e)
        stop?.()
        isCreating.value = false
        if (breaksNameRule(projectName.value.trim()) || breaksNameRule(companyName.value.trim())) {
            errorMessage.value = t('projects.create.error_request_refused')
            step.value = 1
        } else {
            errorCode.value = null
            stripeErrorCode.value = null
            step.value = 5
        }
    })
}

/**
 * Writes the freshly created project onto every invitation it was assigned to.
 *
 * Deliberately not fatal: the project is already created, and a grant that
 * fails here can still be made from Settings › Users, so a failure is reported
 * and the creation flow carries on rather than being rolled back.
 */
async function grantNewProjectToInvitations(projectId: string) {
    const selected = Object.entries(invitationRoles)
        .filter(([, role]) => role === 'member' || role === 'admin')
        .map(([invitationId, role]) => ({ invitationId, role: role as 'member' | 'admin' }))
    if (!selected.length) return

    const failed: string[] = []
    for (const { invitationId, role } of selected) {
        try {
            await tenantsStore.grantProjectToInvitation(invitationId, projectId, role)
        } catch (error) {
            logger.error('Error granting the new project to an invitation', error)
            const invitation = (tenantsStore.invitations || []).find((i) => i.id === invitationId)
            failed.push(invitation?.email || invitationId)
        }
    }
    if (failed.length) {
        useAppStore().toast(
            t('projects.create.pending_invitations_error'),
            failed.join(', '),
            'error',
            5000,
        )
    }
}

async function goToProject() {
    if (!createdProjectId.value) return
    visibleLocal.value = false
    const id = createdProjectId.value
    const route = projectRouteBase(projectType.value)
    resetForm()
    await projectsStore.listProjects(true)
    projectsStore.setCurrentProject(id)
    router.push(`${route}/${id}`)
}
</script>

<style scoped></style>
